import { useMemo, useState, type ReactNode } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { BuildingIcon, PlusIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import {
  applicationTone,
  byActionNeeded,
  waitingOn,
  type Application,
} from '#admin/features/exhibitor/api.ts'
import { applicationsQuery } from '#admin/features/exhibitor/queries.ts'
import { ApplicationBulkBar } from '#admin/features/exhibitor/application-bulk-bar.tsx'
import { ApplicationRowActions } from '#admin/features/exhibitor/application-row-actions.tsx'
import { canBulkVerify, useApplicationSelection } from '#admin/features/exhibitor/bulk-verify.ts'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import { label } from '#admin/features/registrations/api.ts'
import { ApplicationsSkeleton } from '#admin/features/exhibitor/exhibitor-skeletons.tsx'
import {
  APPLICATION_FILTER_LABEL,
  APPLICATION_FILTERS,
  inApplicationFilter,
  matchesApplication,
  type ApplicationFilter,
} from '#admin/features/exhibitor/application-filter.ts'
import { FilteredEmpty, ListFilterBar } from '#admin/components/list-filter.tsx'
import { pageSlice, TablePagination, usePagination } from '#admin/components/table-pagination.tsx'
import {
  SelectionProvider,
  SelectPageBox,
  SelectRowBox,
} from '#admin/components/selection-boxes.tsx'

/**
 * The stand bookings, as a worklist.
 *
 * A **thin** route: it prefetches, renders a table, and owns the page title. What an
 * application *is* — the schema, the ordering, whose turn it is — belongs to
 * `features/exhibitor`, so the dashboard can show the same counts later without importing a
 * route.
 *
 * Sorted by **who is waiting**, not by date. An application in `submitted` is somebody waiting
 * for a human, and newest-first buries it under this morning's drafts. That is also why this
 * table is deliberately not sortable: the order is the product of `byActionNeeded` and means
 * something, and a column header that discarded it would be a regression dressed as a feature.
 */
export const Route = createFileRoute('/_authenticated/exhibition/exhibitors/')({
  pendingComponent: ApplicationsSkeleton,
  loader: ({ context }) => context.queryClient.ensureQueryData(applicationsQuery()),
  component: ApplicationsPage,
})

function ApplicationsPage() {
  const { data } = useSuspenseQuery(applicationsQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useQuery(manifestQuery())
  // A view-only role gets the list and no decisions. The server refuses them anyway.
  const canManage = can(session, 'exhibitor.manage')
  const zone = manifest?.tenant.timezone ?? 'UTC'
  const applications = data.items.toSorted(byActionNeeded)
  const waiting = applications.filter((a) => waitingOn(a.status) === 'you').length

  // Filtered in the browser: the list is already here in full. The order stays `byActionNeeded`.
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<ApplicationFilter>('all')
  const shown = applications.filter((a) => matchesApplication(a, query, filter))
  // One page at a time. A new search or pill starts again at the first page.
  const { page, setPage, pageSize, setPageSize } = usePagination(
    `${filter}|${query.trim().toLowerCase()}`,
  )
  const paged = pageSlice(shown, page, pageSize)

  // Rows selected for a bulk verify.
  const selection = useApplicationSelection(
    `${filter}|${query.trim().toLowerCase()}`,
    shown,
    paged.rows,
  )
  const { selectableKey } = selection

  const clear = () => {
    setQuery('')
    setFilter('all')
  }

  const columns = useMemo<ColumnDef<Application, never>[]>(() => {
    const selectable = selectableKey === '' ? [] : selectableKey.split(',')
    return [
      // Only for a manager, and only while this page has an application to verify.
      ...(canManage && selectable.length > 0
        ? [
            {
              id: 'select',
              meta: { shrink: true },
              // Below `md` each row is a card and this header is its label, so the card says
              // "Select" and the box that selects the whole page is in the wide table only.
              header: () => (
                <>
                  <span className="md:hidden">Select</span>
                  <span className="max-md:hidden">
                    <SelectPageBox
                      ids={selectable}
                      label="Select the applications on this page that wait for verification"
                    />
                  </span>
                </>
              ),
              cell: ({ row }: { row: { original: Application } }) =>
                canBulkVerify(row.original) ? (
                  <SelectRowBox
                    id={row.original.id}
                    label={`Select ${row.original.code ?? row.original.companyName}`}
                  />
                ) : null,
            } satisfies ColumnDef<Application, never>,
          ]
        : []),
      {
        accessorKey: 'code',
        header: 'Reference',
        meta: { primary: true },
        // A draft has no reference yet, and saying so beats an empty cell.
        cell: ({ row }) => (
          <ApplicationLink application={row.original} className="font-mono text-xs">
            {row.original.code ?? 'draft'}
          </ApplicationLink>
        ),
      },
      {
        accessorKey: 'companyName',
        header: 'Company',
        cell: ({ row }) => (
          <ApplicationLink application={row.original}>{row.original.companyName}</ApplicationLink>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge tone={applicationTone(row.original.status)}>
            {label(row.original.status)}
          </StatusBadge>
        ),
      },
      {
        id: 'turn',
        header: 'Waiting on',
        cell: ({ row }) => <span className="text-muted-foreground">{turn(row.original)}</span>,
      },
      {
        id: 'stands',
        header: 'Stands',
        meta: { align: 'end' },
        cell: ({ row }) => row.original.booths?.length ?? '—',
      },
      ...(canManage
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">Actions</span>,
              meta: { align: 'end' as const, shrink: true },
              cell: ({ row }: { row: { original: Application } }) => (
                <ApplicationRowActions application={row.original} zone={zone} />
              ),
            } satisfies ColumnDef<Application, never>,
          ]
        : []),
    ]
  }, [canManage, selectableKey, zone])

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Exhibitors"
        description={
          waiting > 0 ? `${waiting} waiting on you` : 'Nothing is waiting on you right now.'
        }
        {...(canManage
          ? {
              actions: (
                <Button asChild>
                  <Link to="/exhibition/exhibitors/new">
                    <PlusIcon />
                    New exhibitor
                  </Link>
                </Button>
              ),
            }
          : {})}
      />

      {applications.length === 0 ? null : (
        <ListFilterBar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search by company name or reference..."
          inputClassName="sm:max-w-xs"
          status={filter}
          onStatusChange={setFilter}
          statusLabel="Status"
          options={APPLICATION_FILTERS.map((value) => ({
            value,
            label: APPLICATION_FILTER_LABEL[value],
            count: applications.filter((a) => inApplicationFilter(a, value)).length,
          }))}
        />
      )}

      <SelectionProvider ids={selection.ids} set={selection.set}>
        <DataTable
          caption="Exhibitor applications"
          title="Applications"
          // Only with rows: an empty table shows its empty state, and no line under it.
          footer={
            shown.length === 0 ? undefined : (
              <TablePagination
                page={paged.page}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                totalItems={shown.length}
                onPageChange={setPage}
                ariaLabel="Pages of the applications"
              />
            )
          }
          columns={columns}
          data={paged.rows}
          getRowId={(application) => application.id}
          empty={
            applications.length > 0 ? (
              <FilteredEmpty
                message={
                  query.trim() === ''
                    ? 'No exhibitor applications in this status'
                    : `No exhibitor applications matching “${query.trim()}”`
                }
                onClear={clear}
              />
            ) : (
              <EmptyState
                icon={<BuildingIcon />}
                title="No applications yet"
                description="Stand bookings appear here from the moment an exhibitor starts one, including drafts."
              />
            )
          }
        />
      </SelectionProvider>

      {canManage && selection.selectedIds.length > 0 ? (
        <ApplicationBulkBar ids={selection.selectedIds} onClear={selection.clear} />
      ) : null}
    </div>
  )
}

/**
 * Whose move it is, in words.
 *
 * `payment_pending` is the one worth naming explicitly: the status reads as though the
 * organizer owes something, and in fact the invoice is out and the exhibitor owes the money.
 */
function turn(application: Application): string {
  const who = waitingOn(application.status)
  if (who === 'you') return 'You'
  if (who === 'exhibitor') return 'Exhibitor'
  return '—'
}

/** The way into one application, from both of its identifying cells. */
function ApplicationLink({
  application,
  className = '',
  children,
}: {
  application: Application
  className?: string
  children: ReactNode
}) {
  return (
    <Link
      to="/exhibition/exhibitors/$applicationId"
      params={{ applicationId: application.id }}
      className={`hover:underline ${className}`}
    >
      {children}
    </Link>
  )
}
